import {
  createUserWaitReporter,
  reportUserWait,
  UserWaitPendingAction,
} from '$logic/analytics/reporters/user-wait'

export enum UploadSource {
  CHAT = 'chat',
  STORY = 'story',
}

export function createUploadReporter(
  source: UploadSource,
  minimumPendingDurationToReportMS: number = 0,
  reportIfDisconnectedDuringPending = true,
) {
  const uploadReporter = createUserWaitReporter(
    UserWaitPendingAction.UPLOAD,
    undefined,
    undefined,
    false,
  )
  const conversionReporter = createUserWaitReporter(
    UserWaitPendingAction.UPLOAD,
    undefined,
    undefined,
    false,
  )

  let uploadMeta:
    | { duration: number; disconnectedDuringPending: boolean }
    | undefined
  let type = ''
  let size = 0

  const startUpload = (fileType: string, fileSize: number) => {
    uploadReporter.start()
    type = fileType
    size = fileSize
  }

  const startConversion = () => {
    const data = uploadReporter.end()
    if (data) {
      conversionReporter.start()
      uploadMeta = data
    }
  }

  const endWithoutConversion = (additionalData?: Record<string, unknown>) => {
    const data = uploadReporter.end()
    if (!data) return

    const sizeInMB = size / (1024 * 1024)
    const speedMBPerSecond = sizeInMB / (data.duration / 1000)
    const uploadSpeedMBPerSecond = sizeInMB / (data.duration / 1000)

    if (
      data?.duration > minimumPendingDurationToReportMS &&
      (reportIfDisconnectedDuringPending || !data.disconnectedDuringPending)
    )
      reportUserWait(
        UserWaitPendingAction.UPLOAD,
        data.duration,
        data.disconnectedDuringPending,
        {
          fileType: type,
          fileSize: size,
          source,
          speedMBPerSecond,
          uploadSpeedMBPerSecond,
          ...additionalData,
        },
      )
  }

  const end = (additionalData?: Record<string, unknown>) => {
    if (!uploadMeta) {
      endWithoutConversion(additionalData)
      return
    }

    const data = conversionReporter.end()
    if (!data) return
    const totalDuration = data.duration + uploadMeta.duration
    const disconnectedDuringPending =
      data.disconnectedDuringPending || uploadMeta.disconnectedDuringPending

    const sizeInMB = size / (1024 * 1024)
    const speedMBPerSecond = sizeInMB / (totalDuration / 1000)
    const uploadSpeedMBPerSecond = sizeInMB / (uploadMeta.duration / 1000)
    const conversionSpeedMBPerSecond = sizeInMB / (data.duration / 1000)

    if (
      totalDuration > minimumPendingDurationToReportMS &&
      (reportIfDisconnectedDuringPending || !disconnectedDuringPending)
    )
      reportUserWait(
        UserWaitPendingAction.UPLOAD,
        totalDuration,
        disconnectedDuringPending,
        {
          uploadDuration: uploadMeta.duration,
          disconnectedDuringUpload: uploadMeta.disconnectedDuringPending,
          conversionDuration: data.duration,
          disconnectedDuringConversion: data.disconnectedDuringPending,
          fileType: type,
          source,
          speedMBPerSecond,
          uploadSpeedMBPerSecond,
          conversionSpeedMBPerSecond,
          ...additionalData,
        },
      )
  }

  return { startUpload, startConversion, end }
}
